// src/services/admin.js
import API_URL from "../apiConfig";
import { getCurrentSession } from "./auth";
import apiFetch from "../utils/apiFetch";

/* =========================
   Helpers (auth + fetch)
========================= */

function getAuthToken() {
  const session = getCurrentSession();
  return session?.token || null;
}

function buildHeaders({ auth = false } = {}) {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  if (auth) {
    const token = getAuthToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

async function parseJsonSafely(res) {
  const text = await res.text().catch(() => "");
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

function toApiError(res, data) {
  const message =
    data?.message ||
    data?.error ||
    data?.detail ||
    `Error de API (${res.status})`;

  const err = new Error(message);
  err.status = res.status;
  err.data = data;
  return err;
}

async function requestJson(path, { method = "GET", auth = false, body } = {}) {
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: buildHeaders({ auth }),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const data = await parseJsonSafely(res);

  if (!res.ok) throw toApiError(res, data);

  return data;
}

/* =========================
   Admin: Courses
========================= */

export async function adminListCourses() {
  return requestJson("/api/admin/courses", { method: "GET", auth: true });
}

export async function adminGetCourse(slug) {
  return requestJson(`/api/admin/courses/${encodeURIComponent(slug)}`, {
    method: "GET",
    auth: true,
  });
}

export async function adminCreateCourse(payload) {
  const body = {
    module_id: payload.module_id,
    submodule_id: payload.submodule_id,
    format_type: payload.format_type,
    title: payload.title,
    tagline: payload.tagline,
    summary: payload.summary,
    cover_url: payload.cover_url,
    duration_value: payload.duration_value,
    duration_unit: payload.duration_unit,
    status: payload.status,
  };

  return requestJson("/api/admin/courses", {
    method: "POST",
    auth: true,
    body,
  });
}

export async function adminUpdateCourse(slug, payload) {
  const body = {
    module_id: payload.module_id,
    submodule_id: payload.submodule_id,
    format_type: payload.format_type,
    title: payload.title,
    tagline: payload.tagline,
    summary: payload.summary,
    cover_url: payload.cover_url,
    duration_value: payload.duration_value,
    duration_unit: payload.duration_unit,
    status: payload.status,
  };

  return requestJson(`/api/admin/courses/${encodeURIComponent(slug)}`, {
    method: "PUT",
    auth: true,
    body,
  });
}

export async function adminDeleteCourse(slug) {
  return requestJson(`/api/admin/courses/${encodeURIComponent(slug)}`, {
    method: "DELETE",
    auth: true,
  });
}

/* =========================
   Admin: Units
========================= */

export async function adminCreateUnit(slug, payload) {
  const body = {
    id: payload.id,
    title: payload.title,
    minutes: payload.minutes,
    free: !!payload.free,
    pillar: payload.pillar,
    sections: payload.sections || [],
  };

  return requestJson(`/api/admin/courses/${encodeURIComponent(slug)}/units`, {
    method: "POST",
    auth: true,
    body,
  });
}

export async function adminUpdateUnit(slug, id, payload) {
  const body = {
    title: payload.title,
    minutes: payload.minutes,
    free: !!payload.free,
    pillar: payload.pillar,
    sections: payload.sections || [],
  };

  return requestJson(
    `/api/admin/courses/${encodeURIComponent(slug)}/units/${encodeURIComponent(id)}`,
    { method: "PUT", auth: true, body }
  );
}

export async function adminDeleteUnit(slug, unitId) {
  return requestJson(
    `/api/admin/courses/${encodeURIComponent(slug)}/units/${encodeURIComponent(unitId)}`,
    { method: "DELETE", auth: true }
  );
}

/* =========================
   Public read helpers
========================= */

export async function getPublicUnit(slug, id) {
  return requestJson(
    `/api/courses/${encodeURIComponent(slug)}/units/${encodeURIComponent(id)}`,
    { method: "GET", auth: false }
  );
}

export async function getPublicUnitByUnitId(unitId) {
  return requestJson(`/api/content/units/${encodeURIComponent(unitId)}`, {
    method: "GET",
    auth: false,
  });
}

/* =========================
   Producción: Rutas / Media / Templates
========================= */

export async function adminListTemplates() {
  return apiFetch("/api/admin/produccion/templates");
}
export async function adminCreateTemplate(payload) {
  return apiFetch("/api/admin/produccion/templates", {
    method: "POST",
    body: payload,
  });
}
export async function adminUpdateTemplate(id, payload) {
  return apiFetch(`/api/admin/produccion/templates/${id}`, {
    method: "PUT",
    body: payload,
  });
}
export async function adminDeleteTemplate(id) {
  return apiFetch(`/api/admin/produccion/templates/${id}`, {
    method: "DELETE",
  });
}

export async function adminListMedia(search = "") {
  const q = String(search || "").trim();
  const queryString = q ? `?q=${encodeURIComponent(q)}` : "";

  return apiFetch(`/api/admin/produccion/media${queryString}`);
}
export async function adminCreateMedia(payload) {
  return apiFetch("/api/admin/produccion/media", {
    method: "POST",
    body: payload,
  });
}
export async function adminUpdateMedia(id, payload) {
  return apiFetch(`/api/admin/produccion/media/${id}`, {
    method: "PUT",
    body: payload,
  });
}
export async function adminDeleteMedia(id) {
  return apiFetch(`/api/admin/produccion/media/${id}`, {
    method: "DELETE",
  });
}

export async function adminListRutas() {
  return apiFetch("/api/admin/produccion/rutas");
}
export async function adminCreateRuta(payload) {
  return apiFetch("/api/admin/produccion/rutas", {
    method: "POST",
    body: payload,
  });
}
export async function adminUpdateRuta(id, payload) {
  return apiFetch(`/api/admin/produccion/rutas/${id}`, {
    method: "PUT",
    body: payload,
  });
}
export async function adminDeleteRuta(id) {
  return apiFetch(`/api/admin/produccion/rutas/${id}`, {
    method: "DELETE",
  });
}

/* =========================
   Admin: Newsletter
========================= */

export async function adminListNewsletterIssues() {
  return requestJson("/api/admin/newsletter/issues", { method: "GET", auth: true });
}

export async function adminCreateNewsletterIssue(payload) {
  return requestJson("/api/admin/newsletter/issues", {
    method: "POST",
    auth: true,
    body: payload,
  });
}

export async function adminUpdateNewsletterIssue(id, payload) {
  return requestJson(`/api/admin/newsletter/issues/${encodeURIComponent(id)}`, {
    method: "PUT",
    auth: true,
    body: payload,
  });
}

export async function adminSetNewsletterStatus(id, status) {
  return requestJson(`/api/admin/newsletter/issues/${encodeURIComponent(id)}/status`, {
    method: "PUT",
    auth: true,
    body: { status },
  });
}

export async function adminGetNewsletterIssue(id) {
  return requestJson(`/api/admin/newsletter/issues/${encodeURIComponent(id)}`, {
    method: "GET",
    auth: true,
  });
}
